import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import {
  COLUMNS_HIDDEN_BY_DEFAULT,
  COMPANY_NAME_KEY,
  DEAL_NAME_KEY,
  ENGAGEMENT_MANAGER_KEY,
  FINANCIAL_FIELDS_HIDDEN_BY_DEFAULT,
  NOTES_KEY,
  OTHER_CONSULTANT_KEY,
  STATUS_KEY,
  TOGGLEABLE_COLUMNS,
  VALUATION_DATE_KEY,
} from 'common/constants/analyst-dashboards';
import type { ColType } from 'common/types/summaryDefinitions';
import type { ValueType } from 'common/types/summaryDefinitions/valueTypes/valueType';
import { valuationsColumns } from 'dashboard409a/pages/Workboard/common/colConfig';
import type { ValuationView } from 'dashboard409a/types/workboardTableViews/types';
import { resolveColumnOrder } from 'dashboard409a/utilities/valuationsViewConverter';
import EditColumnsPanel from 'pages/Firms/pages/FirmSummary/EditColumnsPanel';

export interface ValuationsNewViewDialogProps {
  open: boolean;
  onClose: () => void;
  onCreateView: (view: ValuationView) => void;
  currentColumnVisibility: Record<string, boolean>;
  currentColumnOrder?: string[];
  currentGroupByDeals: boolean;
  defaultName?: string;
  /** When provided, the dialog operates in edit mode (pre-populated, Save button). */
  initialView?: ValuationView;
}

const DATE_COLUMN_IDS = new Set(['valuation_date', 'created_at', 'supporting_due_date', 'deal_due']);
const FINANCIAL_COLUMN_IDS = new Set(FINANCIAL_FIELDS_HIDDEN_BY_DEFAULT);

const getWorkboardCategory = (id: string): 'financials' | 'dates' | 'process' => {
  if (FINANCIAL_COLUMN_IDS.has(id)) {
    return 'financials';
  }
  if (DATE_COLUMN_IDS.has(id)) {
    return 'dates';
  }
  return 'process';
};

const workboardValueTypes: ValueType[] = valuationsColumns
  .filter(col => TOGGLEABLE_COLUMNS.includes(col.id))
  .map(col => ({
    id: col.id,
    displayName: col.label,
    displayFormat: { type: 'text' as const },
    kind: 'simple' as const,
    dependsOn: [],
    metadata: {
      category: getWorkboardCategory(col.id),
    },
  }));

const workboardValueTypeMap = new Map(workboardValueTypes.map(vt => [vt.id, vt]));

const DEFAULT_WORKBOARD_COLUMNS = TOGGLEABLE_COLUMNS.filter(
  id => !COLUMNS_HIDDEN_BY_DEFAULT.includes(id)
) as ColType[];

const WORKBOARD_REQUIRED_COLUMNS = new Set([
  STATUS_KEY,
  DEAL_NAME_KEY,
  NOTES_KEY,
  OTHER_CONSULTANT_KEY,
  ENGAGEMENT_MANAGER_KEY,
  VALUATION_DATE_KEY,
]);

const getVisibleToggleableColumns = (
  columnVisibility: Record<string, boolean> | undefined,
  columnOrder?: string[]
): ColType[] => {
  const order = resolveColumnOrder(columnOrder).filter(id => TOGGLEABLE_COLUMNS.includes(id));
  const visible = order.filter(id => columnVisibility?.[id] !== false) as ColType[];
  return visible.length > 0 ? visible : DEFAULT_WORKBOARD_COLUMNS;
};

const ValuationsNewViewDialog = ({
  open,
  onClose,
  onCreateView,
  currentColumnVisibility,
  currentColumnOrder,
  currentGroupByDeals,
  defaultName,
  initialView,
}: ValuationsNewViewDialogProps) => {
  const isEditMode = Boolean(initialView);

  const initialColumns = useMemo<ColType[]>(
    () => getVisibleToggleableColumns(currentColumnVisibility, currentColumnOrder),
    [currentColumnVisibility, currentColumnOrder]
  );

  const [viewName, setViewName] = useState('');
  const [editorColumns, setEditorColumns] = useState<ColType[]>(initialColumns);
  const [groupByDeals, setGroupByDeals] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (initialView) {
      setViewName(initialView.name);
      setEditorColumns(
        getVisibleToggleableColumns(initialView.columnVisibility, initialView.columnOrder)
      );
      setGroupByDeals(initialView.groupByDeals ?? false);
    } else {
      setViewName(defaultName ?? '');
      setEditorColumns(initialColumns);
      setGroupByDeals(currentGroupByDeals);
    }
  }, [open, initialView, defaultName, initialColumns, currentGroupByDeals]);

  const handleCreate = () => {
    if (!viewName.trim() || editorColumns.length === 0) {
      return;
    }

    const columnVisibility: Record<string, boolean> = {};
    TOGGLEABLE_COLUMNS.forEach(id => {
      columnVisibility[id] = editorColumns.includes(id as ColType);
    });

    const hiddenToggleable = TOGGLEABLE_COLUMNS.filter(id => !editorColumns.includes(id as ColType));
    const columnOrder = resolveColumnOrder([
      COMPANY_NAME_KEY,
      ...editorColumns,
      ...hiddenToggleable,
    ]);

    onCreateView({
      id: initialView?.id ?? crypto.randomUUID(),
      name: viewName.trim(),
      columnVisibility,
      columnOrder,
      groupByDeals,
    });
    onClose();
  };

  return (
    <Dialog maxWidth="md" fullWidth open={open} onClose={onClose}>
      <DialogTitle>
        {isEditMode ? 'Edit View' : 'Create Valuations View'}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Name your view, choose which columns to display, and drag to reorder them.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Name</Typography>
        <TextField
          fullWidth
          size="small"
          value={viewName}
          onChange={e => setViewName(e.target.value)}
          error={viewName.trim().length === 0 && open}
          helperText={viewName.trim().length === 0 && open ? 'Name is required.' : ' '}
          onKeyDown={e => {
            if (e.key === 'Enter' && viewName.trim()) {
              handleCreate();
            }
          }}
          autoFocus
        />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>Columns</Typography>
        <EditColumnsPanel
          availableColumns={workboardValueTypes}
          allValueTypes={workboardValueTypeMap}
          columns={editorColumns}
          onChange={setEditorColumns}
          defaultColumns={DEFAULT_WORKBOARD_COLUMNS}
          requiredColumns={WORKBOARD_REQUIRED_COLUMNS}
        />

        <FormControlLabel
          labelPlacement="start"
          control={
            <Checkbox
              size="small"
              checked={groupByDeals}
              onChange={(_, checked) => setGroupByDeals(checked)}
            />
          }
          label={<Typography variant="body2">Group Company by Deals</Typography>}
          sx={{ mx: 0, ml: 0, mt: 2 }}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={editorColumns.length === 0 || viewName.trim().length === 0}
        >
          {isEditMode ? 'Save' : 'Create New View'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ValuationsNewViewDialog;
